import { useTranslation } from 'react-i18next';
import { ModuleStringsProvider } from '@platform/modularity/ModuleStringsProvider';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { BaElsewhereNote } from '@platform/engine/insights/BaElsewhereNote';
import { PageFrame } from '@platform/engine/list/header/PageFrame';
import { PageHeader } from '@platform/engine/list/header/PageHeader';
import { useModuleCrumbs } from '@platform/engine/list/header/useModuleCrumbs';
import { AttentionCards } from './AttentionCards';
import type { IModulePageProps } from './IModulePageProps';
import { KpiTile } from './KpiTile';
import { ModuleTablesList } from './ModuleTablesList';
import { OverviewStages } from './OverviewStages';
import { useModuleAdvice } from './useModuleAdvice';
import { usePresetLink } from './usePresetLink';
import './modulePages.css';

/** Скільки порад показує огляд (прототип v4: перші три). */
const TOP_ADVICE = 3;

/**
 * Огляд модуля (прототип v4 isModOverview): крихти, назва модуля над «Огляд», опис головної таблиці; тайли показників в один рядок (разом,
 * відкрито, прострочено, термін цього тижня, не призначено, без змін 14 днів — ведуть до пунктів меню з пресетами), «Таблиці модуля»
 * і «Що потребує уваги» (перші три поради), смуга «Етапи» за статусами.
 */
export function ModuleOverviewPage({ ws, module, rt }: IModulePageProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings(module.module.key);
  const { kpis, stats, advice } = useModuleAdvice(ws, rt);
  const linkOf = usePresetLink(module, rt, strings, stats?.buckets);
  const moduleName = strings.moduleName(module.module.name);
  const crumbs = useModuleCrumbs(module.module.key, moduleName);
  const description = strings.entity(rt.key, 'description', rt.description) || strings.tagline(module.module.tagline);
  const stageLabel = (value: string) => strings.status(rt.key, value, rt.statuses.list.find((s) => s.value === value)?.label);
  const head = <PageHeader eyebrow={moduleName} title={t('modulePages.overview')} description={description} />;
  return (
    <ModuleStringsProvider strings={strings}>
      <PageFrame crumbs={crumbs} head={head}>
        <BaElsewhereNote ws={ws} moduleKey={module.module.key} rtKey={rt.key} />
        <div className="mp">
          <div className="mp-kpis">
            {kpis.map((k) => <KpiTile key={k.key} kpi={k} to={k.key === 'stale' ? undefined : linkOf(k.key === 'total' ? null : k.preset ?? null)} />)}
          </div>
          <div className="mp-grid">
            <ModuleTablesList ws={ws} module={module} />
            {advice.length > 0 && <AttentionCards items={advice.slice(0, TOP_ADVICE)} stageLabel={stageLabel} />}
          </div>
          <OverviewStages rt={rt} buckets={stats?.buckets ?? []} total={stats?.total ?? 0} />
        </div>
      </PageFrame>
    </ModuleStringsProvider>
  );
}
